import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Save, ArrowLeft, Eye, Plus, Trash2, ChevronUp, ChevronDown, Upload, X } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import resizeImage, { IMAGE_ACCEPT, isImageFile } from "@/lib/imageResize";
import InternalLinkField from "@/components/admin/InternalLinkField";
import LinkableTextarea from "@/components/admin/LinkableTextarea";
import RelatedLinksPicker from "@/components/admin/RelatedLinksPicker";
import { LinkSelfContext } from "@/lib/internalLinks";

const SECTION_TYPES = [
  { type: "text_image", label: "Text + Image" },
  { type: "service_cards", label: "Service Cards" },
  { type: "how_it_works", label: "How It Works / Steps" },
  { type: "faq", label: "FAQ" },
  { type: "cta", label: "CTA Section" },
  { type: "photo_gallery", label: "Photo Gallery" },
  { type: "before_after", label: "Before & After" },
  { type: "testimonials", label: "Testimonials" },
  { type: "service_areas", label: "Service Areas" },
];

let _uid = 0;
const uid = () => `s${Date.now()}_${_uid++}`;

function newSection(type) {
  const base = { id: uid(), type, sort_order: 0 };
  const d = {
    text_image: { heading: "", body: "", image_data: "", image_alt: "", image_side: "right", cta_text: "", cta_link: "" },
    service_cards: { heading: "", items: [] },
    how_it_works: { heading: "How It Works", items: [] },
    faq: { heading: "Frequently Asked Questions", items: [] },
    cta: { heading: "", text: "", primary_cta_text: "", primary_cta_link: "", secondary_cta_text: "", secondary_cta_link: "" },
    photo_gallery: { heading: "", items: [] },
    before_after: { heading: "Before & After", items: [] },
    testimonials: { heading: "What Customers Say", items: [] },
    service_areas: { heading: "Areas We Serve", description: "", image_data: "", image_alt: "", items: [] },
  }[type];
  return { ...base, data: d || {} };
}

const input = "mt-1 w-full px-3 py-2 text-sm rounded-lg border border-slate-200 focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30";
const lbl = "block text-xs font-medium text-[#0F172A]";

function Field({ label, value, onChange, textarea, placeholder, testid, linkable, link }) {
  if (link) {
    // CTA link: approved internal page, or phone / email / outside link.
    return <InternalLinkField label={label} value={value} onChange={onChange} mode="cta" testid={testid} />;
  }
  if (linkable) {
    return <LinkableTextarea label={label} value={value} onChange={onChange} placeholder={placeholder} testid={testid} rows={4} />;
  }
  return (
    <label className="block">
      <span className={lbl}>{label}</span>
      {textarea ? (
        <textarea value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} rows={4} data-testid={testid} className={input} />
      ) : (
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid} className={input} />
      )}
    </label>
  );
}

function ImagePicker({ value, onChange, alt, onAlt, altLabel = "Image alt text" }) {
  const ref = useRef(null);
  const onFile = async (file) => {
    if (!file) return;
    if (!isImageFile(file)) { toast.error("Please choose an image file."); return; }
    try { onChange(await resizeImage(file)); toast.success("Image ready — remember to Save."); }
    catch { toast.error("Failed to read image."); }
  };
  return (
    <div>
      <div className="aspect-[16/10] rounded-xl bg-slate-50 border border-dashed border-slate-200 overflow-hidden flex items-center justify-center">
        {value ? <img src={value} alt={alt || ""} className="w-full h-full object-cover" /> : <span className="text-xs text-slate-400">No image</span>}
      </div>
      <div className="mt-2 flex gap-2">
        <button type="button" onClick={() => ref.current?.click()} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-slate-200 hover:bg-slate-50"><Upload className="w-3.5 h-3.5" /> Choose</button>
        {value && <button type="button" onClick={() => onChange("")} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-rose-200 text-rose-700 hover:bg-rose-50"><X className="w-3.5 h-3.5" /> Remove</button>}
        <input ref={ref} type="file" accept={IMAGE_ACCEPT} className="hidden" onChange={(e) => onFile(e.target.files?.[0])} />
      </div>
      {onAlt && <label className="block mt-2"><span className={lbl}>Image alt text</span><input type="text" value={alt || ""} onChange={(e) => onAlt(e.target.value)} className={input} /></label>}
    </div>
  );
}

function RepeatableItems({ items, setItems, fields, addLabel }) {
  const add = () => setItems((prev) => [...(prev || []), { id: uid(), ...Object.fromEntries(fields.map((f) => [f.key, ""])) }]);
  const upd = (i, key, val) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [key]: val } : it)));
  const del = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const move = (i, dir) => setItems((prev) => {
    const j = i + dir;
    if (j < 0 || j >= prev.length) return prev;
    const copy = prev.slice();
    [copy[i], copy[j]] = [copy[j], copy[i]];
    return copy;
  });
  return (
    <div className="space-y-3">
      {(items || []).map((it, i) => (
        <div key={it.id || i} className="rounded-xl border border-slate-200 p-3 bg-slate-50/60">
          <div className="flex justify-between items-center mb-2">
            <span className="text-xs font-semibold text-[#475569]">#{i + 1}</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => move(i, -1)} className="p-1 rounded hover:bg-white"><ChevronUp className="w-4 h-4" /></button>
              <button type="button" onClick={() => move(i, 1)} className="p-1 rounded hover:bg-white"><ChevronDown className="w-4 h-4" /></button>
              <button type="button" onClick={() => del(i)} className="p-1 rounded hover:bg-rose-50 text-rose-600"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
          <div className="grid gap-2">
            {fields.map((f) =>
              f.type === "image" ? (
                <div key={f.key}>
                  {f.label && <span className={lbl}>{f.label}</span>}
                  <ImagePicker
                    value={it[f.key]}
                    onChange={(v) => upd(i, f.key, v)}
                    alt={it[f.altKey || `${f.key}_alt`]}
                    onAlt={(v) => upd(i, f.altKey || `${f.key}_alt`, v)}
                  />
                </div>
              ) : (
                <Field key={f.key} label={f.label} value={it[f.key]} onChange={(v) => upd(i, f.key, v)} textarea={f.type === "textarea"}
                  linkable={f.type === "textarea" && (f.key === "description" || f.key === "answer")} />
              )
            )}
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"><Plus className="w-4 h-4" /> {addLabel}</button>
    </div>
  );
}

function SectionEditor({ section, patchData }) {
  const d = section.data || {};
  const setItems = (updater) =>
    patchData((data) => ({
      ...data,
      items: typeof updater === "function" ? updater(data.items || []) : updater,
    }));
  switch (section.type) {
    case "text_image":
      return (
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <Field label="Heading" value={d.heading} onChange={(v) => patchData({ heading: v })} />
            <Field label="Body" value={d.body} onChange={(v) => patchData({ body: v })} textarea linkable testid="lp-sec-body" />
            <div className="grid grid-cols-2 gap-2">
              <Field label="CTA text (optional)" value={d.cta_text} onChange={(v) => patchData({ cta_text: v })} />
              <Field label="CTA link (optional)" value={d.cta_link} onChange={(v) => patchData({ cta_link: v })} link />
            </div>
            <label className="block"><span className={lbl}>Image side</span>
              <select value={d.image_side || "right"} onChange={(e) => patchData({ image_side: e.target.value })} className={input}>
                <option value="right">Right</option><option value="left">Left</option>
              </select>
            </label>
          </div>
          <ImagePicker value={d.image_data} onChange={(v) => patchData({ image_data: v })} alt={d.image_alt} onAlt={(v) => patchData({ image_alt: v })} />
        </div>
      );
    case "service_cards":
      return (
        <div className="space-y-3">
          <Field label="Section heading" value={d.heading} onChange={(v) => patchData({ heading: v })} />
          <RepeatableItems items={d.items} setItems={setItems} addLabel="Add card" fields={[
            { key: "title", label: "Title" },
            { key: "description", label: "Description", type: "textarea" },
            { key: "image_data", label: "Card image (optional)", type: "image", altKey: "image_alt" },
          ]} />
        </div>
      );
    case "how_it_works":
      return (
        <div className="space-y-3">
          <Field label="Section heading" value={d.heading} onChange={(v) => patchData({ heading: v })} />
          <RepeatableItems items={d.items} setItems={setItems} addLabel="Add step" fields={[
            { key: "title", label: "Step title" },
            { key: "description", label: "Step description", type: "textarea" },
          ]} />
        </div>
      );
    case "faq":
      return (
        <div className="space-y-3">
          <Field label="Section heading" value={d.heading} onChange={(v) => patchData({ heading: v })} />
          <RepeatableItems items={d.items} setItems={setItems} addLabel="Add FAQ" fields={[
            { key: "question", label: "Question" },
            { key: "answer", label: "Answer", type: "textarea" },
          ]} />
        </div>
      );
    case "cta":
      return (
        <div className="space-y-3">
          <Field label="Heading" value={d.heading} onChange={(v) => patchData({ heading: v })} />
          <Field label="Text" value={d.text} onChange={(v) => patchData({ text: v })} textarea linkable />
          <div className="grid grid-cols-2 gap-2">
            <Field label="Primary CTA text" value={d.primary_cta_text} onChange={(v) => patchData({ primary_cta_text: v })} />
            <Field label="Primary CTA link" value={d.primary_cta_link} onChange={(v) => patchData({ primary_cta_link: v })} link />
            <Field label="Secondary CTA text" value={d.secondary_cta_text} onChange={(v) => patchData({ secondary_cta_text: v })} />
            <Field label="Secondary CTA link" value={d.secondary_cta_link} onChange={(v) => patchData({ secondary_cta_link: v })} link />
          </div>
        </div>
      );
    case "photo_gallery":
      return (
        <div className="space-y-3">
          <Field label="Section heading (optional)" value={d.heading} onChange={(v) => patchData({ heading: v })} />
          <RepeatableItems items={d.items} setItems={setItems} addLabel="Add photo" fields={[
            { key: "image_data", label: "Photo", type: "image", altKey: "image_alt" },
          ]} />
        </div>
      );
    case "before_after":
      return (
        <div className="space-y-3">
          <Field label="Section heading (optional)" value={d.heading} onChange={(v) => patchData({ heading: v })} />
          <RepeatableItems items={d.items} setItems={setItems} addLabel="Add before/after pair" fields={[
            { key: "caption", label: "Caption (optional)" },
            { key: "before_image_data", label: "Before image", type: "image", altKey: "before_alt" },
            { key: "after_image_data", label: "After image", type: "image", altKey: "after_alt" },
          ]} />
        </div>
      );
    case "testimonials":
      return (
        <div className="space-y-3">
          <Field label="Section heading (optional)" value={d.heading} onChange={(v) => patchData({ heading: v })} />
          <RepeatableItems items={d.items} setItems={setItems} addLabel="Add testimonial" fields={[
            { key: "name", label: "Customer name" },
            { key: "quote", label: "Testimonial text", type: "textarea" },
            { key: "image_data", label: "Photo (optional)", type: "image", altKey: "image_alt" },
          ]} />
        </div>
      );
    case "service_areas":
      return (
        <div className="space-y-3">
          <Field label="Section heading (optional)" value={d.heading} onChange={(v) => patchData({ heading: v })} />
          <Field label="Description (optional)" value={d.description} onChange={(v) => patchData({ description: v })} textarea linkable />
          <div>
            <span className={lbl}>Section image (optional)</span>
            <ImagePicker value={d.image_data} onChange={(v) => patchData({ image_data: v })} alt={d.image_alt} onAlt={(v) => patchData({ image_alt: v })} />
          </div>
          <RepeatableItems items={d.items} setItems={setItems} addLabel="Add area / city" fields={[
            { key: "name", label: "Area / City" },
            { key: "description", label: "Description (optional)", type: "textarea" },
          ]} />
        </div>
      );
    default:
      return null;
  }
}

export default function AdminLandingPageEditor() {
  const { id } = useParams();
  const isNew = !id;
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [savedId, setSavedId] = useState(id || null);
  const [p, setP] = useState({
    name: "", slug: "", status: "draft", seo_title: "", meta_description: "", index_page: true,
    hero: { h1: "", supporting_text: "", image_data: "", image_alt: "", primary_cta_text: "", primary_cta_link: "", secondary_cta_text: "", secondary_cta_link: "" },
    sections: [],
    related_links: [],
  });
  const [recommended, setRecommended] = useState({});

  useEffect(() => {
    api.get("/admin/internal-links/recommendations").then((r) => setRecommended(r.data?.related || {})).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get(`/admin/landing-pages/${id}`).then((r) => {
      const d = r.data;
      setP({
        name: d.name || "", slug: d.slug || "", status: d.status || "draft",
        seo_title: d.seo_title || "", meta_description: d.meta_description || "", index_page: d.index_page !== false,
        hero: { h1: "", supporting_text: "", image_data: "", image_alt: "", primary_cta_text: "", primary_cta_link: "", secondary_cta_text: "", secondary_cta_link: "", ...(d.hero || {}) },
        sections: (d.sections || []).map((s) => ({ ...s, id: s.id || uid() })),
        related_links: Array.isArray(d.related_links) ? d.related_links : [],
      });
    }).catch((e) => toast.error(apiErrorMessage(e))).finally(() => setLoading(false));
  }, [id, isNew]);

  const setHero = (patch) => setP((prev) => ({ ...prev, hero: { ...prev.hero, ...patch } }));
  const slugify = (v) => (v || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

  const addSection = (type) => setP((prev) => ({ ...prev, sections: [...prev.sections, newSection(type)] }));
  const patchSectionData = (sid, patch) => setP((prev) => ({
    ...prev,
    sections: prev.sections.map((s) =>
      s.id === sid
        ? { ...s, data: typeof patch === "function" ? patch(s.data || {}) : { ...s.data, ...patch } }
        : s
    ),
  }));
  const removeSection = (sid) => setP((prev) => ({ ...prev, sections: prev.sections.filter((s) => s.id !== sid) }));
  const moveSection = (idx, dir) => setP((prev) => {
    const j = idx + dir;
    if (j < 0 || j >= prev.sections.length) return prev;
    const copy = prev.sections.slice();
    [copy[idx], copy[j]] = [copy[j], copy[idx]];
    return { ...prev, sections: copy };
  });

  const buildPayload = () => ({
    name: p.name,
    slug: p.slug || slugify(p.name),
    status: p.status,
    seo_title: p.seo_title,
    meta_description: p.meta_description,
    index_page: p.index_page,
    hero: p.hero,
    sections: p.sections.map((s, i) => ({ id: s.id, type: s.type, sort_order: i + 1, data: s.data || {} })),
    related_links: p.related_links || [],
  });

  const save = async () => {
    if (!p.name.trim()) { toast.error("Page name is required"); return; }
    setSaving(true);
    try {
      const payload = buildPayload();
      let res;
      if (savedId) res = await api.put(`/admin/landing-pages/${savedId}`, payload);
      else res = await api.post("/admin/landing-pages", payload);
      const data = res.data;
      setSavedId(data.id);
      toast.success("Saved");
      if (isNew && data.id) navigate(`/admin/landing-pages/${data.id}`, { replace: true });
      return data.id;
    } catch (e) {
      toast.error(apiErrorMessage(e));
      return null;
    } finally {
      setSaving(false);
    }
  };

  const preview = async () => {
    const pid = await save();
    if (pid) window.open(`/${p.slug || slugify(p.name)}?preview=${pid}`, "_blank");
  };

  if (loading) return <p className="text-sm text-[#475569]">Loading…</p>;

  return (
    <LinkSelfContext.Provider value={`/${p.slug || slugify(p.name)}`}>
    <div data-testid="admin-landing-editor" className="max-w-4xl">
      <div className="mb-6 flex items-center justify-between gap-3">
        <button onClick={() => navigate("/admin/landing-pages")} className="inline-flex items-center gap-1.5 text-sm text-[#475569] hover:text-[#0F172A]"><ArrowLeft className="w-4 h-4" /> Back</button>
        <div className="flex gap-2">
          <button onClick={preview} data-testid="lp-editor-preview" className="inline-flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg border border-slate-200 hover:bg-slate-50"><Eye className="w-4 h-4" /> Preview</button>
          <button onClick={save} disabled={saving} data-testid="lp-editor-save" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#1E3A8A] text-white font-semibold text-sm hover:bg-[#1E40AF] disabled:opacity-60"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save"}</button>
        </div>
      </div>

      <div className="space-y-6">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5">
          <h3 className="font-display font-semibold text-[#0F172A] mb-3">Page & SEO</h3>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Page name" value={p.name} onChange={(v) => setP((prev) => ({ ...prev, name: v, slug: prev.slug || slugify(v) }))} testid="lp-name" />
            <label className="block"><span className={lbl}>URL slug</span>
              <div className="mt-1 flex items-center rounded-lg border border-slate-200 overflow-hidden focus-within:ring-2 focus-within:ring-[#1E3A8A]/30">
                <span className="px-2 text-xs text-slate-400">/</span>
                <input type="text" value={p.slug} onChange={(e) => setP((prev) => ({ ...prev, slug: slugify(e.target.value) }))} data-testid="lp-slug" className="flex-1 px-1 py-2 text-sm focus:outline-none" />
              </div>
            </label>
            <Field label="SEO title" value={p.seo_title} onChange={(v) => setP((prev) => ({ ...prev, seo_title: v }))} testid="lp-seo-title" />
            <Field label="Meta description" value={p.meta_description} onChange={(v) => setP((prev) => ({ ...prev, meta_description: v }))} textarea testid="lp-meta" />
          </div>
          <label className="mt-3 inline-flex items-center gap-2 text-sm text-[#0F172A]">
            <input type="checkbox" checked={p.index_page} onChange={(e) => setP((prev) => ({ ...prev, index_page: e.target.checked }))} data-testid="lp-index" className="w-4 h-4 accent-[#1E3A8A]" />
            Allow search engines to index this page (uncheck for noindex)
          </label>
          <p className="mt-2 text-xs text-[#475569]">Canonical URL is generated automatically: https://pacleans.com/{p.slug || slugify(p.name) || "your-slug"}</p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5">
          <h3 className="font-display font-semibold text-[#0F172A] mb-3">Hero</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Field label="H1 headline" value={p.hero.h1} onChange={(v) => setHero({ h1: v })} testid="lp-h1" />
              <Field label="Supporting text" value={p.hero.supporting_text} onChange={(v) => setHero({ supporting_text: v })} textarea linkable />
              <div className="grid grid-cols-2 gap-2">
                <Field label="Primary CTA text" value={p.hero.primary_cta_text} onChange={(v) => setHero({ primary_cta_text: v })} />
                <Field label="Primary CTA link" value={p.hero.primary_cta_link} onChange={(v) => setHero({ primary_cta_link: v })} link testid="lp-hero-primary-link" />
                <Field label="Secondary CTA text" value={p.hero.secondary_cta_text} onChange={(v) => setHero({ secondary_cta_text: v })} />
                <Field label="Secondary CTA link" value={p.hero.secondary_cta_link} onChange={(v) => setHero({ secondary_cta_link: v })} link />
              </div>
            </div>
            <ImagePicker value={p.hero.image_data} onChange={(v) => setHero({ image_data: v })} alt={p.hero.image_alt} onAlt={(v) => setHero({ image_alt: v })} />
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5" data-testid="lp-related-panel">
          <h3 className="font-display font-semibold text-[#0F172A] mb-3">Related Services</h3>
          <RelatedLinksPicker
            value={p.related_links}
            onChange={(v) => setP((prev) => ({ ...prev, related_links: v }))}
            selfUrl={`/${p.slug || slugify(p.name)}`}
            recommended={recommended[`/${p.slug || slugify(p.name)}`]}
            testid="lp-related"
          />
        </div>

        <div className="bg-white rounded-2xl border border-slate-100 shadow-soft p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-display font-semibold text-[#0F172A]">Content sections</h3>
            <div className="flex items-center gap-2">
              <select id="lp-add-type" data-testid="lp-add-type" className="px-2 py-2 text-sm rounded-lg border border-slate-200">
                {SECTION_TYPES.map((s) => <option key={s.type} value={s.type}>{s.label}</option>)}
              </select>
              <button type="button" data-testid="lp-add-section" onClick={() => addSection(document.getElementById("lp-add-type").value)} className="inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg bg-[#1E3A8A] text-white font-semibold hover:bg-[#1E40AF]"><Plus className="w-4 h-4" /> Add</button>
            </div>
          </div>
          {p.sections.length === 0 ? (
            <p className="text-sm text-[#475569]">No sections yet. Add your first section above.</p>
          ) : (
            <div className="space-y-4">
              {p.sections.map((s, idx) => (
                <div key={s.id} data-testid={`lp-section-${idx}`} className="rounded-xl border border-slate-200 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold uppercase tracking-wider text-[#1E3A8A]">{SECTION_TYPES.find((t) => t.type === s.type)?.label || s.type}</span>
                    <div className="flex gap-1">
                      <button type="button" onClick={() => moveSection(idx, -1)} className="p-1 rounded hover:bg-slate-50"><ChevronUp className="w-4 h-4" /></button>
                      <button type="button" onClick={() => moveSection(idx, 1)} className="p-1 rounded hover:bg-slate-50"><ChevronDown className="w-4 h-4" /></button>
                      <button type="button" onClick={() => removeSection(s.id)} className="p-1 rounded hover:bg-rose-50 text-rose-600"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  </div>
                  <SectionEditor section={s} patchData={(patch) => patchSectionData(s.id, patch)} />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
    </LinkSelfContext.Provider>
  );
}
